import React, { useState, useEffect, useCallback } from 'react';
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

interface SplitCUITFieldProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLElement>) => void;
  required?: boolean;
  requiredAsterisk?: boolean;
  name: string;
  disabled?: boolean;
  id?: string;
  validationDelay?: number;
}

interface CUITParts {
  part1: string;
  part2: string;
  part3: string;
}

const createSyntheticEvent = (name: string, value: string): React.ChangeEvent<HTMLElement> => {
  const target = {
    name,
    value,
    type: 'text',
    checked: false,
    files: null
  } as HTMLInputElement;

  return {
    target,
    currentTarget: target,
    nativeEvent: new Event('change'),
    bubbles: true,
    cancelable: true,
    defaultPrevented: false,
    eventPhase: 2,
    isTrusted: true,
    preventDefault: () => {},
    isDefaultPrevented: () => false,
    stopPropagation: () => {},
    isPropagationStopped: () => false,
    persist: () => {},
    timeStamp: Date.now(),
    type: 'change'
  } as React.ChangeEvent<HTMLElement>;
};

export const SplitCUITField: React.FC<SplitCUITFieldProps> = ({
  label,
  value,
  onChange,
  required = false,
  requiredAsterisk = true,
  name,
  disabled = false,
  id,
  validationDelay = 500
}) => {
  const [parts, setParts] = useState<CUITParts>({
    part1: '',
    part2: '',
    part3: ''
  });
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (value) {
      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length >= 11) {
        setParts({
          part1: cleanValue.slice(0, 2),
          part2: cleanValue.slice(2, 10),
          part3: cleanValue.slice(10, 11)
        });
      } else if (cleanValue.length >= 2) {
        setParts({
          part1: cleanValue.slice(0, 2),
          part2: cleanValue.slice(2),
          part3: ''
        });
      } else {
        setParts({
          part1: cleanValue,
          part2: '',
          part3: ''
        });
      }
    } else {
      setParts({ part1: '', part2: '', part3: '' });
    }
  }, [value]);

  const validateCUIT = useCallback((parts: CUITParts): string => {
    const fullValue = `${parts.part1}${parts.part2}${parts.part3}`;
    
    if (required && (!fullValue || fullValue.trim() === '')) {
      return "Este campo es obligatorio";
    }

    if (fullValue && fullValue.length > 0 && fullValue.length !== 11) {
      return "CUIT debe tener exactamente 11 dígitos";
    }

    return "";
  }, [required]);

  const debouncedValidation = useCallback((parts: CUITParts) => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    const timeout = setTimeout(() => {
      setError(validateCUIT(parts));
    }, validationDelay);

    setValidationTimeout(timeout);
  }, [validationDelay, validateCUIT, validationTimeout]);

  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  const handlePartChange = (partName: keyof CUITParts, inputValue: string, nextInputRef?: React.RefObject<HTMLInputElement>) => {
    const numericValue = inputValue.replace(/\D/g, '');
    
    let maxLength: number;
    switch (partName) {
      case 'part1':
        maxLength = 2;
        break;
      case 'part2':
        maxLength = 8;
        break;
      case 'part3':
        maxLength = 1;
        break;
    }
    
    const limitedValue = numericValue.slice(0, maxLength);
    
    const newParts = {
      ...parts,
      [partName]: limitedValue
    };
    
    setParts(newParts);
    setTouched(true);
    setError("");

    if (limitedValue.length === maxLength && nextInputRef?.current) {
      setTimeout(() => nextInputRef.current?.focus(), 0);
    }
    
    if (required || newParts.part1 || newParts.part2 || newParts.part3) {
      debouncedValidation(newParts);
    }
    
    const fullCuit = `${newParts.part1}${newParts.part2}${newParts.part3}`;
    
    const syntheticEvent = createSyntheticEvent(name, fullCuit);
    onChange(syntheticEvent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, partName: keyof CUITParts, prevInputRef?: React.RefObject<HTMLInputElement>) => {
    if (e.key === 'Backspace' && parts[partName] === '' && prevInputRef?.current) {
      setTimeout(() => {
        prevInputRef.current?.focus();
        const prevValue = prevInputRef.current?.value || '';
        prevInputRef.current?.setSelectionRange(prevValue.length, prevValue.length);
      }, 0);
    }
  };

  const handleBlur = () => {
    if (!required && !parts.part1 && !parts.part2 && !parts.part3) return;
    setTouched(true);
    if (validationTimeout) {
      clearTimeout(validationTimeout);
      setValidationTimeout(null);
    }
    setError(validateCUIT(parts));
  };

  const part1Ref = React.useRef<HTMLInputElement>(null);
  const part2Ref = React.useRef<HTMLInputElement>(null);
  const part3Ref = React.useRef<HTMLInputElement>(null);

  const getInputClassName = (baseClass: string) => {
    return `${baseClass} ${
      error && touched
        ? "border-2 border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    }`;
  };

  return (
    <div className="flex flex-col space-y-1">
      <label
        className="text-base font-medium text-gray-700"
        htmlFor={id || name}
      >
        {label}
        {required && requiredAsterisk && (
          <span className="text-red-500 ml-1" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div className="relative">
      <div className="flex items-center space-x-1 sm:space-x-2">
        <input
          ref={part1Ref}
          type="text"
          value={parts.part1}
          onChange={(e) => handlePartChange('part1', e.target.value, part2Ref)}
          onKeyDown={(e) => handleKeyDown(e, 'part1')}
          onBlur={handleBlur}
          placeholder="XX"
          maxLength={2}
          disabled={disabled}
          className={getInputClassName(
            "w-12 sm:w-16 text-center rounded-md py-2 px-1 sm:px-2 transition-colors duration-200"
          )}
          data-testid={`${name}-part1`}
        />
        
        <span className="text-gray-500 font-mono text-sm sm:text-lg select-none">-</span>
        
        <input
          ref={part2Ref}
          type="text"
          value={parts.part2}
          onChange={(e) => handlePartChange('part2', e.target.value, part3Ref)}
          onKeyDown={(e) => handleKeyDown(e, 'part2', part1Ref)}
          onBlur={handleBlur}
          placeholder="XXXXXXXX"
          maxLength={8}
          disabled={disabled}
          className={getInputClassName(
            "flex-1 text-center rounded-md py-2 px-1 sm:px-2 transition-colors duration-200"
          )}
          data-testid={`${name}-part2`}
        />
        
        <span className="text-gray-500 font-mono text-sm sm:text-lg select-none">-</span>
        
        <input
          ref={part3Ref}
          type="text"
          value={parts.part3}
          onChange={(e) => handlePartChange('part3', e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, 'part3', part2Ref)}
          onBlur={handleBlur}
          placeholder="X"
          maxLength={1}
          disabled={disabled}
          className={getInputClassName(
            "w-8 sm:w-12 text-center rounded-md py-2 px-1 sm:px-2 transition-colors duration-200"
          )}
          data-testid={`${name}-part3`}
        />
     </div>

        {error && touched && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>

      <div className="flex justify-between items-start">
        <p className="text-xs text-gray-500">
          Formato: 11-12345678-9
        </p>
      </div>

      {error && touched && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};